import { setMathMLDisplayAttribute } from '@/lib/utils';
import { InputRule, mergeAttributes, Node } from '@tiptap/core';
import { type KatexOptions } from 'katex';
import { createMathematicsNodeView } from '../mathematics-node-view';

/**
 * Configuration options for the BlockMath extension.
 */
export type BlockMathOptions = {
    /**
     * Renderer to use for block math. Default to 'mathjax' if not specified.
     * Set to 'katex' to use KaTeX instead.
     */
    renderer?: 'mathjax' | 'katex';

    /**
     * KaTeX specific options
     * @see https://katex.org/docs/options.html
     * @example
     * ```ts
     * katexOptions: {
     *   displayMode: true,
     *   throwOnError: false,
     * },
     */
    katexOptions?: KatexOptions;
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        insertBlockMath: {
            /**
             * Inserts a math block node with LaTeX string.
             * @param options - Options for inserting block math.
             * @returns ReturnType
             */
            insertBlockMath: (options: { latex?: string; mathml?: string; pos?: number }) => ReturnType;

            /**
             * Deletes a block math node.
             * @returns ReturnType
             */
            deleteBlockMath: (options?: { pos?: number }) => ReturnType;

            /**
             * Update block math node with optional LaTeX string.
             * @param options - Options for updating block math.
             * @returns ReturnType
             */
            updateBlockMath: (options?: { latex?: string; mathml?: string; pos?: number }) => ReturnType;
        };
    }
}

/**
 * BlockMath is a Tiptap extension for rendering block mathematical expressions using KaTeX.
 * It allows users to insert LaTeX formatted math expressions block within text.
 * It supports rendering, input rules for LaTeX syntax, and click handling for interaction.
 *
 * @example
 * ```javascript
 * import { BlockMath } from '@tiptap/extension-mathematics'
 * import { Editor } from '@tiptap/core'
 *
 * const editor = new Editor({
 *   extensions: [BlockMath],
 * })
 */
export const BlockMathHeadless = Node.create<BlockMathOptions>({
    name: 'blockMath',

    group: 'block',

    atom: true,

    selectable: true,

    addOptions() {
        return {
            renderer: undefined,
            katexOptions: undefined,
        };
    },

    addAttributes() {
        return {
            latex: {
                default: '',
                parseHTML: element => element.getAttribute('data-latex'),
                renderHTML: attributes => {
                    return {
                        'data-latex': attributes.latex,
                    };
                },
            },
            mathml: {
                default: '',
                parseHTML: element =>
                    element.tagName?.toLowerCase() === 'math'
                        ? setMathMLDisplayAttribute((element as HTMLElement).outerHTML, true)
                        : setMathMLDisplayAttribute(element.getAttribute('data-mathml') || '', true),
                renderHTML: attributes => {
                    return {
                        'data-mathml': attributes.mathml,
                    };
                },
            },
        };
    },

    addCommands() {
        return {
            insertBlockMath:
                options =>
                ({ commands, editor }) => {
                    const { latex, mathml, pos } = options;

                    if (!latex) {
                        return false;
                    }

                    return commands.insertContentAt(pos ?? editor.state.selection.from, {
                        type: this.name,
                        attrs: { latex, mathml: setMathMLDisplayAttribute(mathml || '', true) },
                    });
                },

            deleteBlockMath:
                options =>
                ({ editor, tr }) => {
                    const pos = options?.pos ?? editor.state.selection.$from.pos;
                    const node = editor.state.doc.nodeAt(pos);

                    if (!node || node.type.name !== this.name) {
                        return false;
                    }

                    tr.delete(pos, pos + node.nodeSize);
                    return true;
                },

            updateBlockMath:
                options =>
                ({ editor, tr }) => {
                    let pos = options?.pos;

                    if (pos === undefined) {
                        pos = editor.state.selection.$from.pos;
                    }

                    const node = editor.state.doc.nodeAt(pos);

                    if (!node || node.type.name !== this.name) {
                        return false;
                    }

                    tr.setNodeMarkup(pos, this.type, {
                        ...node.attrs,
                        latex: options?.latex ?? '',
                        mathml: setMathMLDisplayAttribute(options?.mathml || '', true),
                    });

                    return true;
                },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="block-math"]',
            },
            {
                tag: 'math',
                getAttrs: el => {
                    if (el.getAttribute('display') !== 'block') return false;
                    return { latex: '' };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'block-math' })];
    },

    addInputRules() {
        return [
            new InputRule({
                find: /^\\\[([^$]+)\\\]$/,
                handler: ({ state, range, match }) => {
                    const [, latex] = match;
                    const { tr } = state;
                    const start = range.from;
                    const end = range.to;

                    tr.replaceWith(start, end, this.type.create({ latex }));
                },
            }),
        ];
    },
});

export const BlockMath = BlockMathHeadless.extend({
    addNodeView() {
        const { renderer, katexOptions } = this.options;
        const editor = this.editor;

        return ({ node, getPos }) => {
            const wrapper = document.createElement('div');
            const innerWrapper = document.createElement('div');
            wrapper.className = 'tiptap-mathematics-render';

            innerWrapper.className = 'block-math-inner';
            wrapper.dataset.type = 'block-math';
            wrapper.setAttribute('data-latex', node.attrs.latex);
            wrapper.appendChild(innerWrapper);

            const isMathJax = !renderer || renderer === 'mathjax' || node.attrs.mathml;

            return createMathematicsNodeView({
                editor,
                node,
                getPos,
                dom: wrapper,
                renderTarget: innerWrapper,
                isMathJax,
                katexOptions,
                inline: false,
                errorClass: 'block-math-error',
            });
        };
    },
});
